import Import from "assets/img/svg/navbar/import";
import Upload from "assets/img/svg/upload";
import ImportData from "components/ImportData";
import ModalBox from "components/Modal";
import { openEditMode } from "components/NewForm/utils";
import Publication from "components/Publication";
import ElementList from "components/shared/ElementList";
import PublicationListItem from "components/shared/PublicationListItem";
import PublicationFormContainer from "containers/PublicationFormContainer";
import { PortalType } from "contexts/portal.context";
import useEditModeLock from "hooks/useEditModeLock";
import useListItemPreview from "hooks/useListItemPreview";
import useSelectFirstRouteItem from "hooks/useSelectFirstRouteItem";
import { usePermissions } from "hooks/usePermissions";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { resetArticles } from "store/articleStore";
import type { AppDispatch } from "store/store";
import { useAppSelector } from "store/store";
import { exportTitleList, fetchTitles, resetTitles, setTitlePreview } from "store/titleStore";
import { fetchTags } from "store/titleStore";
import type { TParamsPagination } from "store/types";

const Publications = () => {
    const portalType = useContext(PortalType);
    const [t] = useTranslation();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { titles: publications, titlesStatus, countTitles } = useAppSelector((state) => state.titles);
    const { channels } = useAppSelector((state) => state.channels);
    const { customFields } = useAppSelector((state) => state.customFields);
    const [showImportTitleModal, setShowImportTitleModal] = useState(false);
    const [showImportTitleWithArticlesModal, setShowImportTitleWithArticlesModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [baseCalculationType, setBaseCalculationType] = useState(channels[0]?.type);
    const tagTitleList = useAppSelector((state) => state.titles.tags);

    const { hasPermissions } = usePermissions();
    const { title } = useAppSelector((state) => state.titles);
    const routePublicationId = params.id ? Number(params.id) : null;
    const [currentPublicationId, setCurrentPublicationId] = useState<number>(routePublicationId ?? -1);

    /**
     * Object that contains refs for each item within list.
     */
    const elementListRef = {};

    useSelectFirstRouteItem(publications, routePublicationId, "/publications", setCurrentPublicationId);

    const { selectItem: setTitle } = useListItemPreview({
        items: publications,
        routeId: routePublicationId,
        currentId: title?.id,
        pathPrefix: "/publications",
        enabled: publications.length > 0,
        onPreview: (selectedPublication) => {
            setCurrentPublicationId(selectedPublication.id);
            dispatch(setTitlePreview(selectedPublication));
        },
    });

    const handleExport = () => dispatch(exportTitleList());

    const popoverMenu = {
        Imports: [
            {
                label: t(`ImportData.ImportPublication_${portalType}`),
                icon: <Upload />,
                action: () => setShowImportTitleModal(true),
                actionDisabled: !hasPermissions(["publication:new"]),
            },
            {
                label: t(`ImportData.ImportPublicationWithArticles_${portalType}`),
                icon: <Upload />,
                action: () => setShowImportTitleWithArticlesModal(true),
                actionDisabled: !hasPermissions(["publication:new"]),
            },
        ],
        Exports: [
            {
                label: t(`ImportData.ExportPublication_${portalType}`),
                icon: <Import />,
                action: () => handleExport(),
            },
        ],
    };

    const orderOptions: { name: string; label: string }[] = [
        { name: "creationDate", label: t("Filters.CreationDate") },
        { name: "releaseDate", label: t("Filters.PublicationDate") },
        { name: "name", label: t("Filters.PublicationName") },
    ];

    const isInitialLoad = titlesStatus === "idle";

    useEffect(() => {
        if (!isInitialLoad) return;
        dispatch(fetchTitles({ limit: 100, sortBy: "creationDate", sortOrder: "DESC" }));
    }, [dispatch, isInitialLoad]);

    useEffect(() => {
        return () => {
            dispatch(resetTitles());
        };
    }, [dispatch]);

    useEditModeLock(editMode);

    useEffect(() => {
        setBaseCalculationType(channels[0]?.type);
    }, [popoverMenu, channels]);

    useEffect(() => {
        if (tagTitleList === null) dispatch(fetchTags());
    }, [tagTitleList, dispatch]);

    return (
        <>
            <ModalBox
                open={showImportTitleModal}
                dictionaryKey="publications"
                onClose={() => setShowImportTitleModal(false)}
                mainTitle={t(`ImportData.ImportPublication_${portalType}`)}
            >
                <ImportData dictionaryKey={"publications"} close={() => setShowImportTitleModal(false)} />
            </ModalBox>
            <ModalBox
                open={showImportTitleWithArticlesModal}
                dictionaryKey="publicationsWithArticles"
                onClose={() => setShowImportTitleWithArticlesModal(false)}
                mainTitle={t(`ImportData.ImportPublicationWithArticles_${portalType}`)}
            >
                <ImportData
                    dictionaryKey={"publicationsWithArticles"}
                    close={() => setShowImportTitleWithArticlesModal(false)}
                    onSucceed={(payload) => {
                        dispatch(resetTitles());
                        dispatch(resetArticles());
                        navigate(`/publications/${payload}`);
                        setCurrentPublicationId(payload);
                    }}
                />
            </ModalBox>
            <ElementList
                elements={publications}
                currentElement={currentPublicationId}
                setCurrentElement={setTitle}
                elementsStatus={titlesStatus}
                elementCount={countTitles}
                elementData={{
                    label: t(`Publications.Publication_${portalType}`, { count: 2 }),
                    actionLabel: t(`Publications.AddPublication_${portalType}`),
                    action: () => navigate("/publications/new"),
                    actionDisabled: !hasPermissions(["publication:new"]),
                }}
                elementSearchFunc={(options: TParamsPagination) => dispatch(fetchTitles(options))}
                isInitialLoad={isInitialLoad}
                popoverMenu={popoverMenu}
                editMode={editMode}
                tagList={tagTitleList}
                orderOptions={orderOptions}
                showTags
            >
                <>
                    {[...publications].map((element, index) => (
                        <PublicationListItem
                            key={index}
                            element={element}
                            currentElementId={currentPublicationId}
                            index={index}
                            action={setTitle}
                            disabled={editMode && element.id != currentPublicationId}
                            ref={(el: HTMLDivElement | null) => (elementListRef[element.id] = el)}
                        />
                    ))}
                </>
            </ElementList>
            <div style={{ width: "100%", position: "relative" }}>
                <Publication
                    editMode={editMode}
                    onEdit={() =>
                        openEditMode({
                            elementListRef: { ...elementListRef },
                            elementId: currentPublicationId.toString(),
                            setEditMode,
                        })
                    }
                />
                {editMode && (
                    <PublicationFormContainer
                        mode="edit-page"
                        editModeActive={editMode}
                        closeEditMode={() => setEditMode(false)}
                    />
                )}
            </div>
        </>
    );
};

export default Publications;
